import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Clock, CheckCircle2, Euro, Mail, Phone, Car, User, Save, ShieldCheck } from "lucide-react";
import { fetchAllQuotesAdmin, updateQuoteAdmin } from "../lib/auth";
import { useAuth } from "../lib/AuthContext";

const formatDateTime = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return String(v);
  }
};

const AdminPage = () => {
  const { admin, loading, adminLogout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [drafts, setDrafts] = useState({}); // { [id]: { quoted_price, admin_notes, admin_status } }
  const [saving, setSaving] = useState(null);
  useEffect(() => {
    if (loading) return;
    if (!admin) {
      navigate("/", { replace: true });
      return;
    }
    (async () => {
      try {
        const data = await fetchAllQuotesAdmin();
        setQuotes(data);
        // Auto-expand a specific quote if arriving from the email button
        const target = searchParams.get("quote");
        if (target && data.some((q) => q.id === target)) {
          setExpanded(target);
          // Scroll into view after paint
          setTimeout(() => {
            const el = document.querySelector(`[data-testid="admin-quote-${target}"]`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 300);
        }
      } catch (_) {
        toast.error("Não foi possível carregar os pedidos");
      }
      setLoadingQuotes(false);
    })();
  }, [admin, loading, navigate, searchParams]);

  const setDraft = (id, patch) => {
    setDrafts((d) => ({ ...d, [id]: { ...(d[id] || {}), ...patch } }));
  };

  const handleSave = async (q, statusOverride) => {
    const draft = drafts[q.id] || {};
    const payload = {};
    const targetStatus = statusOverride ?? draft.admin_status;
    if (targetStatus && targetStatus !== q.admin_status) payload.admin_status = targetStatus;
    if (draft.quoted_price !== undefined && draft.quoted_price !== "" && Number(draft.quoted_price) !== q.quoted_price) {
      payload.quoted_price = Number(draft.quoted_price);
    }
    if (draft.admin_notes !== undefined && draft.admin_notes !== (q.admin_notes || "")) {
      payload.admin_notes = draft.admin_notes;
    }
    // If marking as respondido and no price yet set, force include current draft price if provided
    if (statusOverride === "respondido" && draft.quoted_price !== undefined && draft.quoted_price !== "") {
      payload.quoted_price = Number(draft.quoted_price);
    }
    if (Object.keys(payload).length === 0) {
      toast.info("Nada a atualizar");
      return;
    }
    setSaving(q.id);
    try {
      const updated = await updateQuoteAdmin(q.id, payload);
      setQuotes((qs) => qs.map((x) => (x.id === q.id ? updated : x)));
      setDrafts((d) => { const c = { ...d }; delete c[q.id]; return c; });
      toast.success("Pedido atualizado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar");
    } finally {
      setSaving(null);
    }
  };

  const handleLogout = async () => {
    await adminLogout();
    navigate("/", { replace: true });
  };

  if (loading || !admin) {
    return (
      <main className="min-h-screen bg-[#0E2452] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#5FA8FF] border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }

  const pendentes = quotes.filter((q) => q.admin_status !== "respondido").length;
  const respondidos = quotes.length - pendentes;

  return (
    <main className="min-h-screen bg-[#0E2452] pt-32 pb-20 px-6 md:px-12" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <div className="section-label mb-1" style={{ color: "#5FA8FF" }}>// Painel Admin</div>
            <h1 className="font-display font-bold text-3xl md:text-4xl uppercase text-white leading-tight flex items-center gap-3">
              <ShieldCheck className="w-8 h-8 text-[#5FA8FF]" />
              Pedidos recebidos
            </h1>
            <p className="text-white/60 text-sm font-body mt-1">{admin.email}</p>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="inline-flex items-center gap-2 border border-[#2F568F] text-white/80 hover:border-[#FF5A00] hover:text-white font-body text-sm px-4 py-3 rounded-full transition-all"
          >
            <LogOut className="w-4 h-4" />
            Sair
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-8">
          <div className="rounded-2xl border border-[#2F568F] bg-[#0B1D44] p-4 md:p-5">
            <div className="text-white/50 text-[10px] uppercase tracking-widest font-display">Total</div>
            <div className="font-display font-bold text-3xl text-white mt-1">{quotes.length}</div>
          </div>
          <div className="rounded-2xl border border-[#FF5A00]/40 bg-[#FF5A00]/10 p-4 md:p-5">
            <div className="text-[#FF5A00] text-[10px] uppercase tracking-widest font-display">Pendentes</div>
            <div className="font-display font-bold text-3xl text-[#FF5A00] mt-1">{pendentes}</div>
          </div>
          <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-4 md:p-5">
            <div className="text-emerald-300 text-[10px] uppercase tracking-widest font-display">Respondidos</div>
            <div className="font-display font-bold text-3xl text-emerald-300 mt-1">{respondidos}</div>
          </div>
        </div>

        {loadingQuotes ? (
          <div className="text-white/60 text-sm">A carregar pedidos...</div>
        ) : quotes.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[#2F568F] p-10 text-center text-white/60">
            Ainda não existem pedidos.
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-quotes-list">
            {quotes.map((q) => {
              const respondido = q.admin_status === "respondido";
              const isOpen = expanded === q.id;
              const d = drafts[q.id] || {};
              return (
                <div
                  key={q.id}
                  data-testid={`admin-quote-${q.id}`}
                  className={`rounded-2xl border ${respondido ? "border-emerald-500/40" : "border-[#2F568F]"} bg-[#0B1D44] overflow-hidden`}
                >
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : q.id)}
                    className="w-full text-left p-5 flex items-center gap-4 hover:bg-[#0E2452] transition-colors"
                  >
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest font-display font-bold shrink-0 ${
                        respondido
                          ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                          : "bg-[#FF5A00]/15 text-[#FF5A00] border border-[#FF5A00]/40"
                      }`}
                    >
                      {respondido ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                      {respondido ? "Respondido" : "Pendente"}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold text-white truncate">{q.nome} — {q.servico || "Pedido geral"}</div>
                      <div className="text-white/50 text-xs font-body truncate">{q.email} · {formatDateTime(q.created_at)}</div>
                    </div>
                    {respondido && q.quoted_price != null && (
                      <div className="text-emerald-300 font-display font-bold text-lg shrink-0">€ {q.quoted_price.toFixed(2)}</div>
                    )}
                  </button>

                  {isOpen && (
                    <div className="p-5 pt-0 space-y-4 border-t border-[#2F568F]/40">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                        <div className="flex items-start gap-2 text-sm text-white/80"><User className="w-4 h-4 text-white/40 mt-0.5" /><span>{q.nome}</span></div>
                        <div className="flex items-start gap-2 text-sm text-white/80"><Mail className="w-4 h-4 text-white/40 mt-0.5" /><span>{q.email}</span></div>
                        <div className="flex items-start gap-2 text-sm text-white/80"><Phone className="w-4 h-4 text-white/40 mt-0.5" /><span>{q.telefone}</span></div>
                        <div className="flex items-start gap-2 text-sm text-white/80"><Car className="w-4 h-4 text-white/40 mt-0.5" /><span>{q.veiculo || "—"}{q.matricula ? ` · ${q.matricula}` : ""} · {q.medida_pneu || "—"} · {q.quantidade}x</span></div>
                        {q.customer_email && (
                          <div className="md:col-span-2 text-xs text-emerald-300/80 font-body">🔒 Cliente autenticado: {q.customer_email}</div>
                        )}
                        {q.mensagem && (
                          <div className="md:col-span-2 text-sm text-white/80 bg-[#0E2452] border border-[#2F568F]/60 rounded-xl p-3 whitespace-pre-line">
                            <span className="text-[10px] uppercase tracking-widest text-white/40 font-display block mb-1">Mensagem</span>
                            {q.mensagem}
                          </div>
                        )}
                        {q.marca_preferida && (
                          <div className="text-sm text-white/70">Marca preferida: <span className="text-white">{q.marca_preferida}</span></div>
                        )}
                      </div>

                      {/* Admin editor */}
                      <div className="border-t border-[#2F568F]/40 pt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="text-[10px] uppercase tracking-widest text-white/50 font-display block mb-1">Valor (€)</label>
                          <input
                            data-testid={`admin-price-${q.id}`}
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="ex: 320.00"
                            defaultValue={q.quoted_price ?? ""}
                            onChange={(e) => setDraft(q.id, { quoted_price: e.target.value })}
                            className="w-full bg-[#0E2452] border border-[#2F568F] text-white px-3 py-2 rounded-xl focus:outline-none focus:border-[#5FA8FF]"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="text-[10px] uppercase tracking-widest text-white/50 font-display block mb-1">Nota para o cliente (opcional)</label>
                          <textarea
                            data-testid={`admin-notes-${q.id}`}
                            rows={3}
                            placeholder="ex: Inclui montagem, equilibragem e IVA"
                            defaultValue={q.admin_notes ?? ""}
                            onChange={(e) => setDraft(q.id, { admin_notes: e.target.value })}
                            className="w-full bg-[#0E2452] border border-[#2F568F] text-white px-3 py-2 rounded-xl focus:outline-none focus:border-[#5FA8FF] resize-y text-sm"
                          />
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {!respondido ? (
                          <button
                            data-testid={`admin-respond-${q.id}`}
                            disabled={saving === q.id}
                            onClick={() => handleSave(q, "respondido")}
                            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-[#0E2452] font-display font-bold uppercase tracking-wider text-xs px-4 py-2.5 rounded-full transition-all disabled:opacity-50"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                            Marcar como respondido
                          </button>
                        ) : (
                          <button
                            data-testid={`admin-reopen-${q.id}`}
                            disabled={saving === q.id}
                            onClick={() => handleSave(q, "pendente")}
                            className="inline-flex items-center gap-2 bg-[#FF5A00]/20 hover:bg-[#FF5A00]/40 text-[#FF5A00] border border-[#FF5A00]/60 font-display font-bold uppercase tracking-wider text-xs px-4 py-2.5 rounded-full transition-all disabled:opacity-50"
                          >
                            <Clock className="w-4 h-4" />
                            Reabrir
                          </button>
                        )}
                        <button
                          data-testid={`admin-save-${q.id}`}
                          disabled={saving === q.id}
                          onClick={() => handleSave(q)}
                          className="inline-flex items-center gap-2 border border-[#2F568F] hover:border-[#5FA8FF] text-white font-body text-sm px-4 py-2.5 rounded-full transition-all disabled:opacity-50"
                        >
                          <Save className="w-4 h-4" />
                          Guardar alterações
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
};

export default AdminPage;
